import React from 'react'
import { Star, StarHalf, StarOff } from 'lucide-react'

type RatingStarsProps = {
  value?: number | null
  size?: number
  showValue?: boolean
  className?: string
  color?: string
}

const clampRating = (val?: number | null) => {
  if (!Number.isFinite(val as number)) return 0
  return Math.max(0, Math.min(5, Number(val)))
}

export function RatingStarsDisplay({
  value,
  size = 18,
  showValue = false,
  className = '',
  color = '#f59e0b',
}: RatingStarsProps) {
  const rating = clampRating(value)
  const stars = Array.from({ length: 5 }, (_, idx) => {
    const n = idx + 1
    if (rating >= n - 0.1) return 'full'
    if (rating >= n - 0.75) return 'half'
    return 'empty'
  })
  return (
    <div className={`rating-stars ${className}`}>
      {stars.map((state, idx) => {
        const key = `star-${idx}`
        if (state === 'full') {
          return <Star key={key} size={size} fill={color} color={color} />
        }
        if (state === 'half') {
          return <StarHalf key={key} size={size} color={color} />
        }
        return <StarOff key={key} size={size} color="var(--line)" />
      })}
      {showValue && (
        <span className="rating-stars__value">
          {rating > 0 ? rating.toFixed(1) : '—'}
        </span>
      )}
    </div>
  )
}

type RatingStarsInputProps = {
  value: number
  onChange: (value: number) => void
  disabled?: boolean
  size?: number
}

export function RatingStarsInput({
  value,
  onChange,
  disabled = false,
  size = 24,
}: RatingStarsInputProps) {
  const rating = clampRating(value)
  return (
    <div className="rating-stars-input" role="radiogroup" aria-label="Rating out of five">
      {Array.from({ length: 5 }, (_, idx) => {
        const n = idx + 1
        const active = rating >= n
        return (
          <button
            key={`input-star-${n}`}
            type="button"
            className={`rating-star-btn${active ? ' is-active' : ''}`}
            aria-label={`${n} star${n > 1 ? 's' : ''}`}
            aria-checked={rating === n}
            role="radio"
            disabled={disabled}
            onClick={() => onChange(n)}
          >
            <Star size={size} fill={active ? '#f59e0b' : 'transparent'} color={active ? '#f59e0b' : 'var(--line)'} />
          </button>
        )
      })}
    </div>
  )
}

